import React, { useEffect, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { FormBaseRow } from 'components/Form/Form.styles'
import { Field, Form, FormikProps, withFormik } from 'formik'

import { PlatformConfigurationCountryOverridesTypesDto } from '../../../../api/Trading-Accounts/Types/PlatformConfigurationTypesDto'
import { useAppDispatch, useAppSelector } from '../../../../store'
import {
  fetchCountriesAction,
  fetchTickmillCompaniesAction,
} from '../../../../store/identity/identity.actions'
import {
  fetchCountryOverrides,
  fetchPlatformTypesAction,
} from '../../../../store/trading-accounts/trading-accounts.actions'
import { useApiClient } from '../../../../utils/api/ApiClient'
import { IdentityApiClient } from '../../../../utils/api/identityApi'
import { TradingAccountsApiClient } from '../../../../utils/api/tradingAccountsApi'
import { isEmpty } from '../../../../utils/is-empty'
import {
  ButtonContainer,
  Container,
  FormErrorMessage,
  FormFieldBlock,
  FormFieldGroup,
  FormFieldLabel,
} from '../../../Administration/Administration.styles'
import { CountryOverridesTable } from './CountryOverridesTable'

export interface EditCountryOverridesFormValues {
  tickmillCompanyId?: number
  platformTypeId?: number
  countryOverridesId?: string
  newCountryOverridesId?: string
  tradingAllowed: boolean
}

const EditCountryOverridesFormUI: React.FC<
  FormikProps<EditCountryOverridesFormValues> & OuterProps
> = (props) => {
  const { errors, values, setFieldValue, setFieldError } = props

  const dispatch = useAppDispatch()
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)

  const tickmillCompanies = useAppSelector((state) => state.identity.tickmillCompanies) || []
  const platformTypes = useAppSelector((state) => state.tradingAccounts.platformTypes) || []
  const countryOverrides = useAppSelector((state) => state.tradingAccounts.countryOverrides) || []
  const allCountries = useAppSelector((state) => state.identity.countries) || []

  useEffect(() => {
    if (isEmpty(tickmillCompanies)) {
      dispatch(fetchTickmillCompaniesAction(identityApiClient))
    }
    dispatch(
      fetchCountryOverrides(tradingAccountsApiClient, {
        pageIndex: 1,
        pageSize: 1000,
      })
    )
    if (isEmpty(allCountries)) dispatch(fetchCountriesAction(identityApiClient))
    if (isEmpty(platformTypes)) dispatch(fetchPlatformTypesAction(tradingAccountsApiClient))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const filteredCountryOverrides = useMemo(() => {
    if (!values.tickmillCompanyId || !values.platformTypeId) return []

    return countryOverrides.filter((item) => {
      return (
        Number(item.tickmillCompany.id) === Number(values.tickmillCompanyId) &&
        Number(item.platformType.id) === Number(values.platformTypeId)
      )
    })
  }, [countryOverrides, values.tickmillCompanyId, values.platformTypeId])

  const isCountrySelected = Boolean(values.countryOverridesId)

  const handleDeleteCountry = async (
    countryId: string,
    platformTypeId: number,
    tickmillCompanyId: number,
    tradingAllowed: boolean
  ) => {
    try {
      await tradingAccountsApiClient.deleteCountryOverrides({
        CountryId: countryId,
        PlatformTypeId: platformTypeId,
        TickmillCompanyId: tickmillCompanyId,
        TradingAllowed: tradingAllowed,
      })

      dispatch(
        fetchCountryOverrides(tradingAccountsApiClient, {
          pageIndex: 1,
          pageSize: 1000,
        })
      )
    } catch (e) {
      console.error('Failed to delete country override:', e)
    }
  }

  const handleToggleTradingAllowed = async (
    countryId: string,
    platformTypeId: number,
    tickmillCompanyId: number,
    newValue: boolean
  ) => {
    try {
      await tradingAccountsApiClient.updateCountryOverrides({
        TradingAllowed: newValue,
        CountryId: countryId,
        PlatformTypeId: platformTypeId,
        TickmillCompanyId: tickmillCompanyId,
      })

      dispatch(fetchCountryOverrides(tradingAccountsApiClient))
    } catch (error) {
      console.error('Failed to update tradingAllowed:', error)
    }
  }
  const handleAddCountry = async () => {
    const newCountryId = values.newCountryOverridesId
    if (!newCountryId) {
      setFieldError('newCountryOverridesId', 'Please select a country')
      return
    }
    const alreadyExists = filteredCountryOverrides?.some((item) => item.country.id === newCountryId)

    if (alreadyExists) {
      setFieldError('newCountryOverridesId', 'Already exists')
      return
    }
    try {
      if (newCountryId && values.platformTypeId && values.tickmillCompanyId) {
        await tradingAccountsApiClient.createCountryOverrides({
          CountryId: newCountryId,
          PlatformTypeId: Number(values.platformTypeId),
          TickmillCompanyId: Number(values.tickmillCompanyId),
          TradingAllowed: values.tradingAllowed,
        })
        dispatch(
          fetchCountryOverrides(tradingAccountsApiClient, {
            pageIndex: 1,
            pageSize: 1000,
          })
        )
      }
    } catch (e) {
      console.log({ e })
    }
  }

  useEffect(() => {
    if (isCountrySelected) {
      const selected = filteredCountryOverrides.find(
        (item) => item.country.id === values.countryOverridesId
      )
      setFieldValue('tradingAllowed', selected?.tradingAllowed ?? true)
    } else {
      setFieldValue('tradingAllowed', true)
    }
  }, [values.countryOverridesId, setFieldValue])

  return (
    <Container>
      <Form>
        <FormFieldBlock>
          <FormBaseRow>
            <FormFieldLabel>Country Overrides</FormFieldLabel>
            <FormFieldGroup style={{ gap: '8px' }}>
              <CountryOverridesTable
                data={filteredCountryOverrides}
                onDeleteCountryOverride={handleDeleteCountry}
                onToggleTradingAllowed={handleToggleTradingAllowed}
              />
            </FormFieldGroup>
          </FormBaseRow>
        </FormFieldBlock>

        <FormFieldBlock>
          <FormBaseRow>
            <FormFieldLabel>Add New Country Override</FormFieldLabel>
            <FormFieldGroup style={{ gap: '8px' }}>
              <Field component='select' name='newCountryOverridesId'>
                <option value=''>Add new country override</option>
                {allCountries.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Field>
              <ButtonContainer>
                <Button key='button' type='button' onClick={handleAddCountry}>
                  Add Country
                </Button>
              </ButtonContainer>
            </FormFieldGroup>
          </FormBaseRow>
          {errors.newCountryOverridesId && (
            <FormErrorMessage>{errors.newCountryOverridesId}</FormErrorMessage>
          )}
        </FormFieldBlock>
      </Form>
    </Container>
  )
}

interface OuterProps {
  data?: PlatformConfigurationCountryOverridesTypesDto
}

export const EditPlatformConfigurationCountryOverridesForm = withFormik<
  OuterProps,
  EditCountryOverridesFormValues
>({
  mapPropsToValues: ({ data }) => {
    const currentPlatform: Partial<PlatformConfigurationCountryOverridesTypesDto> = data || {}
    return {
      tickmillCompanyId: currentPlatform.tickmillCompany?.id,
      platformTypeId: currentPlatform.platformType?.id,
      countryOverridesId: currentPlatform.countriesOverrides?.country.id,
      newCountryOverridesId: currentPlatform.newCountryOverrides?.id,
      tradingAllowed: currentPlatform.tradingAllowed ?? false,
    }
  },
  enableReinitialize: true,
  handleSubmit: () => {},
})(EditCountryOverridesFormUI)
